// Documentation-export buttons (HTML / Markdown) for the Vocabulary and
// Ontology pages — the desktop documentation feature, generated server-side
// from the selected graph and downloaded as a file — and, on the Vocabulary
// page, the vocabulary as a spreadsheet the import reads back.

import { useState } from 'react';
import { downloadDocExport } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';
import { buttonVariants } from '@/components/ui/Button';
import { Message } from '@/components/ui/Message';

export function DocExportButtons({
  kind,
  graph,
}: {
  kind: 'vocab' | 'ontology';
  graph: string | null;
}) {
  // A read the server grants a guest (QuerySelect), so it goes out with or
  // without a sign-in.
  const { token, ready } = useApiSession();
  const [busy, setBusy] = useState<'html' | 'markdown' | 'csv' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function run(format: 'html' | 'markdown' | 'csv') {
    if (!graph || busy) return;
    setBusy(format);
    setError(null);
    setNote(null);
    const r = await downloadDocExport(kind, graph, format, token);
    if (!r.ok) setError(`Nothing was downloaded: ${r.message}`);
    else setNote(r.note);
    setBusy(null);
  }

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-tres-text-secondary">Documentation:</span>
      <button
        type="button"
        onClick={() => void run('html')}
        disabled={!ready || !graph || busy !== null}
        title="Download this graph's documentation as a styled HTML page"
        className={buttonVariants({ variant: 'outline', size: 'sm' })}
      >
        {busy === 'html' ? 'Generating…' : 'HTML'}
      </button>
      <button
        type="button"
        onClick={() => void run('markdown')}
        disabled={!ready || !graph || busy !== null}
        title="Download this graph's documentation as Markdown (imports into Word)"
        className={buttonVariants({ variant: 'outline', size: 'sm' })}
      >
        {busy === 'markdown' ? 'Generating…' : 'Markdown'}
      </button>
      {kind === 'vocab' && (
        <button
          type="button"
          onClick={() => void run('csv')}
          disabled={!ready || !graph || busy !== null}
          title="Download every term as a spreadsheet (CSV): its class, broader term, definition and alternative labels. The spreadsheet import reads the file back."
          className={buttonVariants({ variant: 'outline', size: 'sm' })}
        >
          {busy === 'csv' ? 'Generating…' : 'Spreadsheet'}
        </button>
      )}
      {error && (
        <Message kind="warning" density="compact">
          {error}
        </Message>
      )}
      {note && <span className="text-xs text-tres-text-secondary">{note}</span>}
    </div>
  );
}
